import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import type { FullContest } from '@betterpool/api-types';

import { KYCContext } from '~/components/providers/KYCProvider';
import { LocationProviderQueryKeys } from '~/components/providers/query';
import { reportEvent } from '~/domains/analytics';
import { UserContext } from '~/components/providers/UserProvider';
import { LocationContext } from '~/components/providers/LocationProvider';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { ContestQueryKeys } from '~/domains/contest/constants/query';

import { ProtectionGuardType, type LocationRestrictionModalOverrides } from '../types';
import { calculateAge } from '../utils';

import runAccessCheck from './accessChecks';

const LOADING_TIMEOUT_MS = 20_000;
const LOCATION_TIMEOUT_DESCRIPTION = 'Location check timed out';

type UseKYCProtectedGuardProps = {
  action: string;
  protectionLevels: ProtectionGuardType[];
  allowAnnonymous?: boolean;
  contestId?: string;
  checkOnMount?: boolean;
  isContestLocationRestrictedOverride?: boolean;
  locationRestrictionModal?: LocationRestrictionModalOverrides;
  redirectOnClose?: string | ((redirectTo?: string) => string);
  onAccessFailed?: VoidFunction;
  requireKycReturn?: boolean;
};

function useProtectedGuard({
  action,
  protectionLevels,
  allowAnnonymous = false,
  contestId,
  checkOnMount,
  redirectOnClose,
  isContestLocationRestrictedOverride,
  locationRestrictionModal,
  onAccessFailed,
  requireKycReturn,
}: UseKYCProtectedGuardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAccessible, setIsAccessible] = useState(false);

  const { getRedirect, userId, userDetails } = useContext(UserContext);
  // Using "isFetching" query value because isInitialLoading doesn't fire when new data is fetched...
  const { KYCStatus, isKYCfetching: isKYCloading } = useContext(KYCContext);
  const {
    allowedLocation,
    currentCountry,
    currentLocation,
    initialLocationLoaded,
    locationCountry,
    locationError,
    locationState,
    locationStatus,
    locationErrors,
    geoPermissionStatus,
    isGeoComplyEnabled,
    isLoading: isLocationLoading,
    refetchLocationToken,
    verifyingLocation,
  } = useContext(LocationContext);
  const { openModalUnique, closeModalsByType } = useContext(RootModalsContext);

  const contestQueryKey = ContestQueryKeys.CONTEST_DETAIL({ contestId, userId });
  const queryClient = useQueryClient();
  const isContestLoading = queryClient.isFetching(contestQueryKey);
  const contestData = queryClient.getQueryData<FullContest>(contestQueryKey);

  const router = useRouter();
  const redirectTo = (getRedirect && getRedirect()) || window.location.href;
  const userAge = useMemo(() => calculateAge(userDetails?.dateOfBirth), [userDetails]);

  // Using refs to provide pointers to get around the react lifecycle providing stale props (not updating during the check)
  // Note: isLoading states are most important to be fresh, we can probably live without the other
  const dataRef = useRef({
    contestData,
    geoPermissionStatus,
    isGeoComplyEnabled,
    isContestLoading: false,
    isKYCloading: false,
    isLocationRestrictedOverride: isContestLocationRestrictedOverride,
    isLocationLoading: false,
    KYCStatus: null,
    verifyingLocation,
    allowedLocation,
    currentCountry,
    currentLocation,
    initialLocationLoaded,
    locationCountry,
    locationError,
    locationErrors,
    locationState,
    locationStatus,
    refetchLocationToken,
    userAge,
    userId,
  });
  dataRef.current.contestData = contestData;
  dataRef.current.geoPermissionStatus = geoPermissionStatus;
  dataRef.current.isGeoComplyEnabled = isGeoComplyEnabled;
  dataRef.current.isContestLoading = isContestLoading > 0;
  dataRef.current.isKYCloading = isKYCloading;
  dataRef.current.isLocationRestrictedOverride = isContestLocationRestrictedOverride;
  dataRef.current.isLocationLoading = isLocationLoading;
  dataRef.current.KYCStatus = KYCStatus;
  dataRef.current.verifyingLocation = verifyingLocation;
  dataRef.current.allowedLocation = allowedLocation;
  dataRef.current.currentCountry = currentCountry;
  dataRef.current.currentLocation = currentLocation;
  dataRef.current.initialLocationLoaded = initialLocationLoaded;
  dataRef.current.locationCountry = locationCountry;
  dataRef.current.locationError = locationError;
  dataRef.current.locationErrors = locationErrors;
  dataRef.current.locationState = locationState;
  dataRef.current.locationStatus = locationStatus;
  dataRef.current.refetchLocationToken = refetchLocationToken;
  dataRef.current.userAge = userAge;
  dataRef.current.userId = userId;

  // Universal onClose handler for all error modals
  const handleOnAccessFailed = useCallback(
    (isSubmit: boolean) => {
      onAccessFailed?.();

      if (isSubmit) return;

      if (redirectOnClose) {
        if (typeof redirectOnClose === 'function') {
          const redirectUrl = redirectOnClose(redirectTo);
          router.push(redirectUrl);
        } else {
          router.push(redirectOnClose);
        }
      }
    },
    [redirectOnClose, redirectTo, router]
  );

  // This is the main function that runs the protection checks on demand with optional callback
  const runProtectionChecks = async (callback?: VoidFunction) =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise<boolean | void>(async (resolve) => {
      if (isKYCloading || isLocationLoading || isContestLoading) {
        setIsLoading(true);
      }

      // Get fresh contest data if required (first load might not have location token included)
      // Do not fetch contest data if the contest restriction override is set (i.e. from QuickPicks)
      if (
        protectionLevels.includes(ProtectionGuardType.CONTEST) &&
        (isContestLocationRestrictedOverride === undefined ||
          isContestLocationRestrictedOverride === null)
      ) {
        setIsLoading(true);
        await queryClient.refetchQueries({ queryKey: contestQueryKey });
      }

      // Start polling for the loading states.
      // Note we have to start using refs to drill props into the interval function scope
      const startedAt = Date.now();
      const checkLoadingInterval = setInterval(() => {
        if (Date.now() - startedAt > LOADING_TIMEOUT_MS) {
          clearInterval(checkLoadingInterval);

          reportEvent('ProtectionGuard > Location Check Timed Out', {
            action,
            contestId,
            geoPermissionStatus: dataRef.current.geoPermissionStatus,
            isGeoComplyEnabled: dataRef.current.isGeoComplyEnabled,
            userId: dataRef.current.userId,
          });

          openModalUnique({
            type: 'LocationErrorModal',
            locationErrors: [{ description: LOCATION_TIMEOUT_DESCRIPTION }],
            onRecheckLocation: async () => {
              closeModalsByType('LocationErrorModal');
              await dataRef.current.refetchLocationToken(true);
              await runProtectionChecks(callback);
            },
            queryKey: LocationProviderQueryKeys.LOCATION_TOKEN(
              dataRef.current.userId,
              dataRef.current.geoPermissionStatus
            ),
          }).then(handleOnAccessFailed);

          setIsLoading(false);
          setIsAccessible(false);
          resolve(false);
          return;
        }

        if (
          (!dataRef.current.isKYCloading || !protectionLevels.includes(ProtectionGuardType.KYC)) &&
          (!dataRef.current.isLocationLoading ||
            !(
              protectionLevels.includes(ProtectionGuardType.LOCATION) ||
              protectionLevels.includes(ProtectionGuardType.GEOCOMPLY)
            )) &&
          (!dataRef.current.isContestLoading ||
            !protectionLevels.includes(ProtectionGuardType.CONTEST))
        ) {
          clearInterval(checkLoadingInterval);

          // Once loading is complete, run the checks and resolve
          const checkResult = runAccessCheck(protectionLevels, {
            ...dataRef.current,
            action,
            allowAnnonymous,
            contestId,
            locationRestrictionModal,
            redirectTo,
            requireKycReturn,
            closeModalsByType,
            forceRerender: () => runProtectionChecks(callback),
            handleOnAccessFailed,
            openModalUnique,
          });

          setIsLoading(false);
          setIsAccessible(checkResult);

          if (checkResult && callback) resolve(callback());
          else resolve(checkResult);
        }
      }, 100);
    });

  // Run the checks on mount if desired
  useEffect(() => {
    if (checkOnMount) {
      runProtectionChecks();
    }
  }, []);

  return {
    runProtectionChecks,
    isLoading,
    isAccessible,
    isError: !!locationErrors?.length,
  };
}

export default useProtectedGuard;
